import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { lang, t, getRouteUrl } = useLanguage();

  const homeUrl = getRouteUrl('home');
  const navLinks = t.nav;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  const isActive = (path) => {
    if (path === '/' || path === '/en') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const iconColor = 'bg-stone-900';

  return (
    <>
      {/* Vertically Rotated Text on the absolute left edge */}
      <div className="fixed left-6 bottom-24 z-40 hidden lg:block pointer-events-none">
        <div className="rotate-[-90deg] origin-left translate-y-full">
          <p className="text-[10px] font-bold tracking-[0.5em] uppercase whitespace-nowrap text-stone-500">
            {t.common.experienceBadge}
          </p>
        </div>
      </div>

      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 h-24 flex items-center ${
          isScrolled 
            ? 'bg-[#faf8f5]/95 backdrop-blur-md border-b border-stone-200/60 shadow-sm' 
            : 'bg-[#faf8f5]/80 backdrop-blur-sm border-b border-stone-200/40'
        }`}
      >
        <div className="w-full px-6 md:px-12 h-full">
          <div className="flex justify-between items-center h-full">
            {/* Left side: Burger + Logo with Divider */}
            <div className="flex items-center h-full">
              <button
                onClick={() => setIsOpen(true)}
                className="flex items-center pr-6 md:pr-10 h-full border-r border-stone-200/80 transition-colors duration-500 group focus:outline-none"
                aria-label={t.common.menuOpen}
              >
                <div className="flex flex-col space-y-1.5 overflow-hidden">
                  <span className={`w-8 h-[1.5px] transition-transform duration-500 group-hover:translate-x-2 ${iconColor}`}></span>
                  <span className={`w-5 h-[1.5px] transition-transform duration-500 group-hover:translate-x-4 ${iconColor}`}></span>
                  <span className={`w-8 h-[1.5px] transition-transform duration-500 group-hover:translate-x-2 ${iconColor}`}></span>
                </div>
              </button>

              <Link to={homeUrl} className="pl-6 md:pl-10 flex items-center h-full">
                <span className="text-lg md:text-xl font-serif font-bold tracking-[0.2em] uppercase whitespace-nowrap text-stone-950">
                  Eliana Wine
                </span>
              </Link>
            </div>

            {/* Right side: Desktop Quick Nav Links + Language Switcher */}
            <div className="flex items-center space-x-6 md:space-x-8">
              <div className="hidden md:flex items-center space-x-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={`text-xs uppercase tracking-[0.2em] font-medium transition-colors ${
                      isActive(link.href) ? 'text-[#581c24] font-bold' : 'text-stone-700 hover:text-[#581c24]'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>

              {/* Language Switcher Button Group */}
              <div className="flex items-center">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Full Screen Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[60] bg-[#faf6f0] text-stone-900 flex flex-col overflow-hidden"
          >
            {/* Menu Header */}
            <div className="flex items-center justify-between px-6 md:px-12 h-24 border-b border-stone-200/80">
              <div className="flex items-center h-full">
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex items-center space-x-6 group text-stone-900 focus:outline-none h-full pr-10 border-r border-stone-200/80"
                  aria-label={t.common.menuClose}
                >
                  <div className="relative w-8 h-8 flex items-center justify-center">
                    <X className="w-6 h-6 stroke-[1.5] transition-transform duration-500 group-hover:rotate-90 text-stone-900" />
                  </div>
                  <span className="text-[10px] font-bold tracking-[0.4em] uppercase text-stone-900">
                    {t.common.menuClose}
                  </span>
                </button>
                
                <Link to={homeUrl} onClick={() => setIsOpen(false)} className="pl-8 md:pl-12">
                  <span className="text-lg font-serif font-bold tracking-[0.2em] text-stone-950 uppercase whitespace-nowrap">
                    Eliana Wine
                  </span>
                </Link>
              </div>

              <div>
                <LanguageSwitcher variant="default" onSelect={() => setIsOpen(false)} />
              </div>
            </div>

            <div className="flex-1 flex flex-col items-center justify-start relative overflow-y-auto px-6 pt-16 md:pt-20 pb-20">
              <nav className="flex flex-col items-center space-y-4 max-w-4xl w-full">
                {navLinks.map((link, idx) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + idx * 0.08, duration: 0.6 }}
                    className="w-full text-center"
                  >
                    <Link
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className="group inline-flex flex-col items-center py-3"
                    >
                      <span className="text-[10px] font-bold tracking-[0.4em] text-[#581c24] mb-1 uppercase">
                        0{idx + 1} —
                      </span>
                      <div className="relative overflow-hidden">
                        <span className={`text-3xl md:text-5xl font-serif font-light uppercase tracking-tight transition-all duration-300 block ${
                          isActive(link.href) ? 'text-[#581c24] font-normal' : 'text-stone-900 group-hover:text-[#581c24]'
                        }`}>
                          {link.name}
                        </span>
                        <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[#581c24] transition-all duration-500 group-hover:w-full"></span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </nav>

              {/* Language Selection in Menu */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.6 }}
                className="mt-10"
              >
                <LanguageSwitcher variant="menu" onSelect={() => setIsOpen(false)} />
              </motion.div>

              {/* Experience Info Section */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.6 }}
                className="mt-12 text-center max-w-xl bg-[#f2ede4] p-8 rounded-xl border border-stone-200/80"
              >
                <div className="space-y-4">
                  <p className="text-[10px] font-bold tracking-[0.4em] text-[#581c24] uppercase">
                    {t.navbar.expertiseBadge}
                  </p>
                  <div className="space-y-2">
                    <p className="text-stone-800 text-xs font-medium tracking-wide uppercase leading-relaxed">
                      {t.navbar.exp1}
                    </p>
                    <p className="text-stone-800 text-xs font-medium tracking-wide uppercase leading-relaxed">
                      {t.navbar.exp2}
                    </p>
                    <p className="text-stone-800 text-xs font-medium tracking-wide uppercase leading-relaxed">
                      {t.navbar.exp3}
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Bottom Info Section (Centered) */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.65, duration: 0.8 }}
                className="mt-12 w-full max-w-4xl border-t border-stone-200/80 pt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-center"
              >
                <div className="space-y-1">
                  <p className="text-[9px] font-bold tracking-[0.4em] text-stone-500 uppercase">
                    {t.common.name === 'Navn' ? 'Kontakt' : 'Contact'}
                  </p>
                  <a href={`mailto:${t.contactInfo.email}`} className="block text-stone-900 hover:text-[#581c24] transition-colors text-xs font-medium tracking-widest uppercase">
                    {t.contactInfo.email}
                  </a>
                </div>

                <div className="space-y-1">
                  <p className="text-[9px] font-bold tracking-[0.4em] text-stone-500 uppercase">
                    {t.common.followUs}
                  </p>
                  <div className="flex justify-center space-x-6">
                    <a href="#" className="text-stone-900 hover:text-[#581c24] transition-colors text-xs font-medium uppercase tracking-[0.2em]">Instagram</a>
                    <a href="#" className="text-stone-900 hover:text-[#581c24] transition-colors text-xs font-medium uppercase tracking-[0.2em]">Facebook</a>
                  </div>
                </div>

                <div className="space-y-1">
                  <p className="text-[9px] font-bold tracking-[0.4em] text-stone-500 uppercase">
                    {t.common.location}
                  </p>
                  <p className="text-stone-900 text-xs font-medium uppercase tracking-[0.2em]">
                    {t.contactInfo.locationText}
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
